"use client"

import { useEffect, useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { MigrationDetail } from "@/types/payroll"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { formatDateTime } from "@/lib/locale"
import type { TranslationKey } from "@/translations"
import { ChevronsUpDown, Loader2 } from "lucide-react"
import { RowMeta, StatusChip } from "@/components/row-card"
import { STATUS_LABEL_KEYS, STATUS_VARIANT } from "./migrations-page"
import { EditDetailsDialog } from "./edit-details-dialog"

interface Props {
  detail: MigrationDetail | null
  onClose: () => void
  onActionCompleted: () => void
}

/** Pretty-print a provider body for reading; show it raw if it will not parse. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/** Initials for the drawer's avatar circle, as Timesheet Hub headers show. */
function initials(name: string | null | undefined, fallback: number): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return `#${fallback}`.slice(0, 3)
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("")
}

/**
 * Worker detail as a right slide-out drawer — the Timesheet Hub pattern
 * (drawers instead of centered popups): avatar + name header with status
 * chips, a grey label/value summary panel, small-caps section headings, and
 * a pinned footer with the primary action as a navy pill plus text actions.
 */
export function MigrationDetailDialog({ detail, onClose, onActionCompleted }: Props) {
  const { payrollApi } = useApi()
  const { toast } = useToast()
  const { t, language } = useTranslation()
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const [preview, setPreview] = useState<{ body: string | null; reason: string | null } | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  // Once a send has been attempted the record holds the real body, so there is
  // nothing to work out. Before that, ask the server what would go — computed
  // by the same adapter serialization the send uses, never re-derived here.
  const alreadySent = Boolean(detail?.providerRequestPayload)
  // A completed record with no stored body has two innocent causes: it predates
  // FBO storing them, or the worker already existed at the provider so no create
  // was sent. The panel must not tell an auditor that nothing went out, and must
  // not pick one cause it cannot tell apart — "What {provider} answered", just
  // below, says which one it was.
  // Statuses that mean something was posted. Created covers a completion whose
  // body predates this, or the dedupe path where the worker already existed;
  // Sent means a create was issued whose outcome was never persisted, so a
  // legacy record parked there did post. A record that failed at the exists
  // check never posted and never confirmed the worker was there, so it is
  // deliberately not here — "nothing has been sent yet" is true of it.
  const sentWithoutStoredBody =
    !alreadySent && (detail?.status === "Created" || detail?.status === "Sent")

  const recordKey = detail
    ? `${detail.provider}/${detail.userProfileId}/${detail.internalOrganizationId}`
    : null
  // The preview costs a read plus a re-map, and the panel it feeds is collapsed
  // by default, so it waits for the first expand. Latched against the record
  // rather than a bare boolean: this component renders null between records but
  // stays mounted, so a bare latch would leak into the next worker and fetch
  // for them unasked. Keying it also means collapsing does not re-fetch.
  const [advancedOpenedFor, setAdvancedOpenedFor] = useState<string | null>(null)
  const previewKey = !alreadySent && advancedOpenedFor && advancedOpenedFor === recordKey ? recordKey : null

  useEffect(() => {
    if (!detail || !previewKey) {
      setPreview(null)
      return
    }

    let cancelled = false
    setIsLoadingPreview(true)
    payrollApi
      .get<{ requestBody: string | null; reason: string | null }>(
        API_ENDPOINTS.PAYROLL.MIGRATION_REQUEST_PREVIEW(
          detail.provider,
          detail.userProfileId,
          detail.internalOrganizationId,
        ),
      )
      .then((r) => {
        if (!cancelled) setPreview({ body: r.requestBody ?? null, reason: r.reason ?? null })
      })
      .catch((err) => {
        // The server's own reason wins; only the fallback is translated.
        if (!cancelled) {
          setPreview({ body: null, reason: getApiErrorDetail(err) ?? t("detail.previewFailed") })
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPreview(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey])

  if (!detail) {
    // Drop the expand latch whenever the drawer is closed, so re-opening the
    // same worker starts collapsed AND unfetched. Done here rather than in the
    // Sheet's close handler because the drawer also closes by the parent
    // setting detail to null after an action, which never reaches that handler.
    // Adjusting state during render is the documented way to react to a prop
    // change; it is guarded, so it settles in one extra pass and commits no
    // stale previewKey.
    if (advancedOpenedFor !== null) {
      setAdvancedOpenedFor(null)
    }
    return null
  }

  const canRemediate = detail.status !== "Created"
  const isGated = detail.status === "WaitingForWorkOrder"
  const tone = STATUS_VARIANT[detail.status] ?? "neutral"
  const statusKey: TranslationKey | undefined = STATUS_LABEL_KEYS[detail.status]
  const workerName = detail.workerName || t("workers.profileNumber", { id: detail.userProfileId })

  const runAction = async (action: string, doneMessage: string, endpoint: string) => {
    setBusyAction(action)
    try {
      await payrollApi.post(endpoint, {})
      toast({ title: doneMessage })
      onActionCompleted()
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("detail.toast.actionFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
    } finally {
      setBusyAction(null)
    }
  }

  if (isEditingDetails) {
    return (
      <EditDetailsDialog
        detail={detail}
        onClose={() => setIsEditingDetails(false)}
        onSaved={() => {
          setIsEditingDetails(false)
          onActionCompleted()
        }}
      />
    )
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:w-[680px] sm:max-w-[92vw]">
        <SheetHeader>
          <div className="flex items-start gap-3 pr-8">
            <div
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#dedfe1] text-xs font-semibold text-muted-foreground"
            >
              {initials(detail.workerName, detail.userProfileId)}
            </div>
            <div className="min-w-0">
              <SheetTitle>{workerName}</SheetTitle>
              <SheetDescription>
                {detail.providerEnvironmentName === "production"
                  ? t("detail.goingToLive", { provider: detail.provider })
                  : t("detail.goingToEnv", {
                      provider: detail.provider,
                      environment: detail.providerEnvironmentName,
                    })}
              </SheetDescription>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip tone={tone}>{statusKey ? t(statusKey) : detail.status}</StatusChip>
            {detail.internalOrganizationCode ? (
              <StatusChip tone="neutral">{detail.internalOrganizationCode}</StatusChip>
            ) : detail.internalOrganizationId > 0 ? (
              <StatusChip tone="neutral">#{detail.internalOrganizationId}</StatusChip>
            ) : null}
          </div>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4 text-sm">
          {/* Grey summary panel — the Timesheet Hub label/value grid. */}
          <div className="grid grid-cols-2 gap-3 rounded-[10px] bg-[var(--bg-neutral-subtle)] p-4 sm:grid-cols-3">
            <RowMeta label={t("detail.meta.fboProfile")}>#{detail.userProfileId}</RowMeta>
            <RowMeta label={t("detail.meta.employeeIdAt", { provider: detail.provider })}>
              {detail.providerEmployeeId || "—"}
            </RowMeta>
            <RowMeta label={t("detail.meta.triesSoFar")}>{detail.attemptCount}</RowMeta>
            <RowMeta label={t("common.added")}>{formatDateTime(detail.createdOn, language)}</RowMeta>
            {detail.completedOn && (
              <RowMeta label={t("detail.meta.accepted")}>{formatDateTime(detail.completedOn, language)}</RowMeta>
            )}
            {detail.lastActionBy && <RowMeta label={t("detail.meta.lastActionBy")}>{detail.lastActionBy}</RowMeta>}
            {detail.resolvedBy && <RowMeta label={t("detail.meta.handledBy")}>{detail.resolvedBy}</RowMeta>}
          </div>

          {detail.status === "Created" && (
            <div className="rounded-[10px] border border-[var(--border-success)] bg-[var(--bg-success)] p-3 text-[var(--gw-text-success)]">
              <p className="text-xs">
                {detail.completedOn
                  ? t("detail.acceptedOn", {
                      provider: detail.provider,
                      date: formatDateTime(detail.completedOn, language),
                    })
                  : t("detail.acceptedNoDate", { provider: detail.provider })}
              </p>
            </div>
          )}

          {(detail.error ?? detail.lastProcessingError) && (
            <section className="space-y-1.5">
              <h4 className="th-section-label">{t("detail.whatWentWrong")}</h4>
              <div className="rounded-[10px] border border-[var(--border-error)] bg-[var(--bg-error)] p-3">
                {/* Server-produced error text — shown verbatim, never translated. */}
                <p className="whitespace-pre-wrap text-xs text-[var(--gw-text-error)]">
                  {detail.error ?? detail.lastProcessingError}
                </p>
              </div>
            </section>
          )}

          {detail.gateReason && (
            <section className="space-y-1.5">
              <h4 className="th-section-label">{t("detail.whyWaiting")}</h4>
              <div className="rounded-[10px] border border-[var(--border-warning)] bg-[var(--bg-warning)] p-3">
                {/* Gate reason comes from the server — shown verbatim. */}
                <p className="whitespace-pre-wrap text-xs">{detail.gateReason}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("detail.gateHint")}</p>
              </div>
            </section>
          )}

          <Collapsible onOpenChange={(open) => open && setAdvancedOpenedFor(recordKey)}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="px-0 text-xs text-muted-foreground">
                <ChevronsUpDown className="mr-1 h-3 w-3" />
                {t("detail.advanced")}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3">
              <div>
                <h4 className="th-section-label mb-1">
                  {alreadySent
                    ? t("detail.sentHeading", { provider: detail.provider })
                    : t("detail.wouldSendHeading", { provider: detail.provider })}
                </h4>
                {sentWithoutStoredBody && (
                  <p className="mb-1 text-xs text-muted-foreground">
                    {detail.status === "Sent"
                      ? t("detail.noStoredBodySent", { provider: detail.provider })
                      : t("detail.noStoredBodyCreated", { provider: detail.provider })}
                  </p>
                )}
                {/*
                  Always the provider's own wire shape — the body that was
                  posted, or the body that would be. The prepared payload is
                  deliberately not shown here: it is a different shape, and
                  showing it under a "sent" heading is what made this panel
                  misleading. Edit details is where the prepared data is read
                  and changed.
                */}
                {alreadySent ? (
                  <>
                    <pre className="max-h-56 overflow-auto rounded-[10px] bg-[var(--bg-neutral-subtle)] p-3 text-xs">
                      {prettyJson(detail.providerRequestPayload!)}
                    </pre>
                    {detail.completedOn && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("detail.sentOn", { date: formatDateTime(detail.completedOn, language) })}
                      </p>
                    )}
                  </>
                ) : isLoadingPreview ? (
                  <p className="rounded-[10px] bg-[var(--bg-neutral-subtle)] p-3 text-xs text-muted-foreground">
                    {t("detail.workingOutPreview")}
                  </p>
                ) : preview?.body ? (
                  <>
                    <pre className="max-h-56 overflow-auto rounded-[10px] bg-[var(--bg-neutral-subtle)] p-3 text-xs">
                      {prettyJson(preview.body)}
                    </pre>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {sentWithoutStoredBody
                        ? t("detail.previewWouldSendNow")
                        : t("detail.previewNothingSentYet")}
                    </p>
                  </>
                ) : (
                  <p className="rounded-[10px] bg-[var(--bg-neutral-subtle)] p-3 text-xs">
                    {preview?.reason ??
                      (sentWithoutStoredBody ? t("detail.noSentBodyRecorded") : t("detail.nothingSentYet"))}
                  </p>
                )}
              </div>
              {detail.responsePayload && (
                <div>
                  <h4 className="th-section-label mb-1">{t("detail.answerHeading", { provider: detail.provider })}</h4>
                  <pre className="max-h-40 overflow-auto rounded-[10px] bg-[var(--bg-neutral-subtle)] p-3 text-xs">
                    {detail.responsePayload}
                  </pre>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {t("detail.reference", { reference: detail.correlationId })}
              </p>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {!canRemediate ? (
          <SheetFooter>
            <Button className="rounded-full" variant="ghost" onClick={onClose}>
              {t("common.close")}
            </Button>
          </SheetFooter>
        ) : (
          <SheetFooter>
            {isGated ? (
              <Button
                className="rounded-full"
                onClick={() =>
                  void runAction(
                    "push",
                    t("detail.toast.sending"),
                    API_ENDPOINTS.PAYROLL.MIGRATION_PUSH(detail.provider, detail.userProfileId, detail.internalOrganizationId)
                  )
                }
                disabled={busyAction !== null}
              >
                {busyAction === "push" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {t("detail.action.sendNow")}
              </Button>
            ) : null}
            <Button
              className="rounded-full"
              variant={isGated ? "ghost" : "default"}
              onClick={() =>
                void runAction(
                  "retry",
                  isGated ? t("detail.toast.checkingWorkOrder") : t("detail.toast.tryingAgain"),
                  API_ENDPOINTS.PAYROLL.MIGRATION_RETRY(detail.provider, detail.userProfileId, detail.internalOrganizationId)
                )
              }
              disabled={busyAction !== null}
            >
              {busyAction === "retry" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isGated ? t("detail.action.checkWorkOrder") : t("common.tryAgain")}
            </Button>
            <Button className="rounded-full" variant="ghost" onClick={() => setIsEditingDetails(true)} disabled={busyAction !== null}>
              {t("detail.action.editDetails")}
            </Button>
            <Button
              className="rounded-full"
              variant="ghost"
              onClick={() =>
                void runAction(
                  "resolve",
                  t("detail.toast.markedHandled"),
                  API_ENDPOINTS.PAYROLL.MIGRATION_RESOLVE(detail.provider, detail.userProfileId, detail.internalOrganizationId)
                )
              }
              disabled={busyAction !== null}
              title={t("detail.action.markHandledHint")}
            >
              {t("detail.action.markHandled")}
            </Button>
            <Button
              className="rounded-full"
              variant="ghost"
              onClick={() =>
                void runAction(
                  "ignore",
                  t("detail.toast.willNotSend"),
                  API_ENDPOINTS.PAYROLL.MIGRATION_IGNORE(detail.provider, detail.userProfileId, detail.internalOrganizationId)
                )
              }
              disabled={busyAction !== null}
              title={t("detail.action.dontSendHint")}
            >
              {t("detail.action.dontSend")}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
